// SPDX-License-Identifier: AGPL-3.0-or-later

import type {GuildComboboxOption} from '@app/features/app/components/dialogs/shared/GuildComboboxRenderers';
import {
	ServerTagBadgeGlyph,
	colorNumberToCss,
	SERVER_TAG_DEFAULT_BADGE_ID,
	SERVER_TAG_DEFAULT_COLOR,
	type ServerTagBadgeId,
} from '@app/features/guild/components/server_tag/ServerTagBadgeIcons';
import Guilds from '@app/features/guild/state/Guilds';
import {
	SERVER_TAG_NONE_OPTION_DESCRIPTOR,
	SERVER_TAG_PROFILE_HINT_DESCRIPTOR,
	SERVER_TAG_PROFILE_SECTION_DESCRIPTOR,
} from '@app/features/guild/utils/ServerTagMessageDescriptors';
import {Combobox} from '@app/features/ui/components/form/FormCombobox';
import styles from '@app/features/user/components/modals/tabs/my_profile_tab/ServerTagProfileSection.module.css';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {isServerTagBadgeId} from '@fluxer/constants/src/ServerTagConstants';
import {GuildFeatures} from '@fluxer/constants/src/GuildConstants';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useCallback} from 'react';

interface ServerTagProfileSectionProps {
	value: string | null;
	onChange: (guildId: string | null) => void;
	disabled?: boolean;
}

function resolveBadgeId(value: string | null | undefined): ServerTagBadgeId {
	return value && isServerTagBadgeId(value) ? value : SERVER_TAG_DEFAULT_BADGE_ID;
}

export const ServerTagProfileSection = observer(
	({value, onChange, disabled = false}: ServerTagProfileSectionProps) => {
		const {i18n} = useLingui();
		const taggedGuilds = Guilds.getGuilds().filter(
			(guild) => guild.features.has(GuildFeatures.SERVER_TAG) && Boolean(guild.tagName),
		);

		const renderGuildRow = useCallback(
			(option: GuildComboboxOption) => {
				const isNone = !option.value;
				const guild = option.value ? taggedGuilds.find((g) => g.id === option.value) : null;
				const iconUrl = option.icon ? AvatarUtils.getGuildIconURL({id: option.value, icon: option.icon}) : null;
				const initial = option.label.charAt(0).toUpperCase();
				const accent = colorNumberToCss(guild?.tagColor ?? SERVER_TAG_DEFAULT_COLOR);
				const badgeId = resolveBadgeId(guild?.tagBadgeId);
				return (
					<div
						className={clsx(styles.optionRow, isNone && styles.optionRowNone)}
						data-flx="user.my-profile-tab.server-tag-profile-section.option-row"
					>
						{!isNone &&
							(iconUrl ? (
								<div
									className={styles.guildAvatar}
									style={{backgroundImage: `url(${iconUrl})`}}
									aria-hidden
								/>
							) : (
								<div className={styles.guildAvatarPlaceholder} aria-hidden>
									{initial}
								</div>
							))}
						<span className={styles.optionLabel}>{option.label}</span>
						{guild?.tagName ? (
							<span className={styles.tagPreview}>
								<ServerTagBadgeGlyph badgeId={badgeId} size={12} accent={accent} />
								<span className={styles.tagPreviewText}>{guild.tagName}</span>
							</span>
						) : null}
					</div>
				);
			},
			[taggedGuilds],
		);

		if (taggedGuilds.length === 0) {
			return null;
		}

		const options: Array<GuildComboboxOption> = [
			{value: '', label: i18n._(SERVER_TAG_NONE_OPTION_DESCRIPTOR)},
			...taggedGuilds.map((guild) => ({
				value: guild.id,
				label: guild.name || '',
				icon: guild.icon ?? null,
			})),
		];

		return (
			<div className={styles.container} data-flx="user.my-profile-tab.server-tag-profile-section.container">
				<Combobox
					label={i18n._(SERVER_TAG_PROFILE_SECTION_DESCRIPTOR)}
					value={value || ''}
					options={options}
					onChange={(next) => onChange(next === '' ? null : next)}
					disabled={disabled}
					className={clsx(disabled && styles.disabled)}
					renderOption={renderGuildRow}
					renderValue={(option) => (option ? renderGuildRow(option) : null)}
					data-flx="user.my-profile-tab.server-tag-profile-section.combobox"
				/>
				<p className={styles.hint}>{i18n._(SERVER_TAG_PROFILE_HINT_DESCRIPTOR)}</p>
			</div>
		);
	},
);
